import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, X, Info } from 'lucide-react';
import { User } from '../types';
import { usePwaInstallPrompt } from '../hooks/usePwaInstallPrompt';
import { useCustomerHasAnyAppointment } from '../hooks/useCustomerHasAnyAppointment';
import { isCustomer } from '../utils/roleUtils';
import { PwaHowToInstallModal } from './PwaHowToInstallModal';

const DISMISS_KEY = 'pwa_install_banner_dismissed_v1';
const DISMISS_TTL_DAYS = 7;
const DISMISS_TTL_MS = DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000; // 7 days in milliseconds
const AUTO_DISMISS_AFTER_MS = 30 * 1000;

interface DismissData {
  timestamp: number;
}

const isDismissed = (): boolean => {
  try {
    const value = localStorage.getItem(DISMISS_KEY);
    if (!value) return false;
    
    // Handle legacy format (just '1')
    if (value === '1') {
      // Migrate to new format with current timestamp
      const dismissData: DismissData = { timestamp: Date.now() };
      localStorage.setItem(DISMISS_KEY, JSON.stringify(dismissData));
      return true;
    }
    
    // Parse new format with timestamp
    const dismissData: DismissData = JSON.parse(value);
    const now = Date.now();
    const elapsed = now - dismissData.timestamp;
    
    // If more than TTL has passed, consider it not dismissed
    if (elapsed > DISMISS_TTL_MS) {
      localStorage.removeItem(DISMISS_KEY);
      return false;
    }
    
    return true;
  } catch {
    // If localStorage is unavailable or parsing fails, default to not dismissed
    return false;
  }
};

const saveDismissed = (): void => {
  try {
    const dismissData: DismissData = { timestamp: Date.now() };
    localStorage.setItem(DISMISS_KEY, JSON.stringify(dismissData));
  } catch {
    // If localStorage is unavailable, silently fail
  }
};

const shouldHideOnPath = (pathname: string): boolean => {
  // Avoid showing this on auth flows even if a logged-in user lands there.
  const hiddenPrefixes = ['/login', '/register', '/forgot-password', '/reset-password'];
  return hiddenPrefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
};

export const PwaInstallBanner: React.FC<{ user: User | null }> = ({ user }) => {
  const location = useLocation();
  const { runningAsPwa, iosSafari, canPromptInstall, promptInstall } = usePwaInstallPrompt();

  const [dismissed, setDismissed] = useState<boolean>(() => isDismissed());
  const [showHowToModal, setShowHowToModal] = useState(false);
  const [hasWaitedForPrompt, setHasWaitedForPrompt] = useState(false);

  // Wait for the beforeinstallprompt event to potentially fire after page load
  // This is especially important after page refreshes in production
  useEffect(() => {
    if (!user || runningAsPwa || iosSafari) return;
    
    // If we already have the prompt, no need to wait
    if (canPromptInstall) {
      setHasWaitedForPrompt(true);
      return;
    }
    
    // Give the browser time to fire the beforeinstallprompt event
    // This event might be delayed in production environments
    const timer = setTimeout(() => {
      setHasWaitedForPrompt(true);
    }, 2000); // Wait 2 seconds for the event to fire

    return () => clearTimeout(timer);
  }, [user, runningAsPwa, iosSafari, canPromptInstall]);

  // Re-check localStorage when user changes to ensure dismissed state is accurate
  useEffect(() => {
    if (!user) return;
    setDismissed(isDismissed());
  }, [user]);

  const eligibleBusinessUser = useMemo(() => {
    if (!user?.roles?.length) return false;
    // Requirement: eligible for *any* active business role (owner/professional/custom, etc.)
    return user.roles.some(
      (role) => !!role.businessId && role.isActive && role.businessRoleActive !== false
    );
  }, [user]);

  const shouldCheckCustomerAppointments = useMemo(() => {
    if (!user) return false;
    if (eligibleBusinessUser) return false;
    // Requirement: customers must have booked at least one appointment.
    return isCustomer(user);
  }, [user, eligibleBusinessUser]);

  const { hasAny: customerHasAnyAppointment } = useCustomerHasAnyAppointment(
    user,
    shouldCheckCustomerAppointments
  );

  // For customers: show if they have appointments (true), or if we're still checking (null)
  // We'll be more lenient - show for customers while loading, but hide if explicitly false
  const eligibleCustomer =
    !!user &&
    shouldCheckCustomerAppointments &&
    (customerHasAnyAppointment === true || customerHasAnyAppointment === null);

  const eligible = eligibleBusinessUser || eligibleCustomer;

  const dismiss = useCallback(() => {
    saveDismissed();
    setDismissed(true);
  }, []);

  const handleInstall = () => {
    // Always show the how-to modal instead of triggering browser install dialog
    setShowHowToModal(true);
  };

  const isBannerVisible = useMemo(() => {
    if (!user) return false;
    if (dismissed) return false;
    if (runningAsPwa) return false;
    if (!eligible) return false;
    if (shouldHideOnPath(location.pathname)) return false;
    // Only show if we can actually install, or if it's iOS Safari (manual install).
    if (!iosSafari && !canPromptInstall && !hasWaitedForPrompt) return false;
    return true;
  }, [
    user,
    dismissed,
    runningAsPwa,
    eligible,
    location.pathname,
    iosSafari,
    canPromptInstall,
    hasWaitedForPrompt,
  ]);

  // Same behavior as clicking the dismiss (X) control: persist + hide banner
  useEffect(() => {
    if (!isBannerVisible) return;
    const timer = window.setTimeout(() => {
      dismiss();
    }, AUTO_DISMISS_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [isBannerVisible, dismiss]);

  if (!isBannerVisible) return null;

  const dismissIconButtonClassName =
    'p-1.5 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-black/5 flex items-center justify-center shrink-0';

  return (
    <>
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9998] w-[min(720px,calc(100%-2rem))]">
        <div
          className="relative border shadow-lg rounded-xl px-4 py-3 flex flex-wrap sm:flex-nowrap items-center gap-3"
          style={{
            backgroundColor: '#E8F1FB', // solid light navy
            borderColor: '#B9D2EE',
          }}
        >
          {/* Mobile: top-right; desktop: duplicate control lives in actions row (sm:flex) so How to Install layout matches original */}
          <button
            type="button"
            className={`absolute top-3 right-3 z-10 sm:hidden ${dismissIconButtonClassName}`}
            onClick={dismiss}
            aria-label="Dismiss install banner"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex-shrink-0">
            <Download className="w-5 h-5 text-gray-700" />
          </div>
          <div className="flex-1 min-w-0 pr-10 sm:pr-0">
            <div className="font-semibold text-gray-900">Install for faster access</div>
            <div className="text-sm text-gray-600">
              Add this app to your Home Screen for quicker, app-like access.
            </div>
          </div>

          <div className="w-full sm:w-auto flex flex-row gap-2 items-center justify-center sm:justify-end sm:ml-auto">
            {iosSafari ? (
              <button
                type="button"
                className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-800 hover:bg-gray-50 flex items-center gap-1.5"
                onClick={() => setShowHowToModal(true)}
              >
                <Info className="w-4 h-4" />
                How to install
              </button>
            ) : (
              <button
                type="button"
                className="px-3 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5"
                style={{ backgroundColor: '#0B4778' }}
                onClick={handleInstall}
                title="View installation instructions"
              >
                <Info className="w-4 h-4" />
                How to Install
              </button>
            )}

            <button
              type="button"
              className={`hidden sm:flex ${dismissIconButtonClassName}`}
              onClick={dismiss}
              aria-label="Dismiss install banner"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <PwaHowToInstallModal
        isOpen={showHowToModal}
        onClose={() => setShowHowToModal(false)}
      />
    </>
  );
};

